import React from 'react';

import { useAPI } from '../../api';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryParamState } from '../../helpers/query';

interface ResetInfo {
	twoFactor: {
		required: boolean;
		verified: boolean;
	};
}

interface APIResponse {
	error?: string;
}

export default function AuthResetPassword() {
	const navigate = useNavigate();
	const api = useAPI();

	const [token] = useQueryParamState('token');

	const [error, setError] = React.useState<string | null>(null);
	const [success, setSuccess] = React.useState<string | null>(null);

	const [resetInfo, setResetInfo] = React.useState<ResetInfo | null>(null);
	const [code, setCode] = React.useState('');
	const [recoveryCode, setRecoveryCode] = React.useState('');
	const [recoveryMode, setRecoveryMode] = React.useState(false);
	const [password, setPassword] = React.useState('');
	const [password2, setPassword2] = React.useState('');

	async function resetPassword() {
		if (password !== password2) {
			setError('Passwords do not match');
			return;
		}

		const json = await api.post<APIResponse>(`/api/auth/recovery/resetpassword/${token}/reset`, { password });

		if (json.error) {
			setError(json.error);
		} else {
			setError(null);
			setSuccess('Password reset successfully! Redirecting to login page...');
			setTimeout(() => navigate('/auth/login'), 2000);
		}
	}

	async function verifyTwoFactorCode() {
		const json = await api.post<ResetInfo & APIResponse>(`/api/auth/recovery/resetpassword/${token}/twofactor/verify`, { code });

		if (json.error) {
			setError(json.error);
		} else {
			setError(null);
			setResetInfo(json);
		}
	}

	async function verifyTwoFactorRecoveryCode() {
		const json = await api.post<ResetInfo & APIResponse>(`/api/auth/recovery/resetpassword/${token}/twofactor/recovery`, { recoveryCode });

		if (json.error) {
			setError(json.error);
		} else {
			setError(null);
			setResetInfo(json);
		}
	}

	function loadResetInfo() {
		api.get<ResetInfo>(`/api/auth/recovery/resetpassword/${token}`)
		.then(json => {
			setResetInfo(json);
		});
	}

	React.useEffect(() => {
		loadResetInfo();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return token ? (
		<div className="centeredContainer">
			<div>
				<h1>Reset Password</h1>

				{resetInfo && (
					<div>
						{error ? <div style={{color: "red"}}>{error}</div> : null}
						{success ? <div style={{color: "green"}}>{success}</div> : null}

						{
							resetInfo.twoFactor.required && !resetInfo.twoFactor.verified ? (
								recoveryMode
								? (
									<div>
										<div className="spacedFormGroup">
											<label>Recovery Code</label>
											<input type="text" name="recoveryCode" size={17} style={{ fontSize: '150%', fontFamily: 'monospace' }} value={recoveryCode} onChange={e => setRecoveryCode(e.target.value)} />
										</div>

										<div className="spacedFormGroup">
											<button onClick={verifyTwoFactorRecoveryCode}>Verify Recovery Code</button>
										</div>

										<div className="spacedFormGroup">
											<button className="link" onClick={() => setRecoveryMode(false)}>Use Two Factor Code Instead</button>
										</div>
									</div>
								)
								: (
									<div>
										<div className="spacedFormGroup">
											<label>Two Factor Code</label>
											<input type="text" name="code" size={6} style={{ fontSize: '250%', fontFamily: 'monospace' }} value={code} onChange={e => setCode(e.target.value)} />
										</div>

										<div className="spacedFormGroup">
											<button onClick={verifyTwoFactorCode}>Verify Two Factor Code</button>
										</div>

										<div className="spacedFormGroup">
											<button className="link" onClick={() => setRecoveryMode(true)}>Use Recovery Code Instead</button>
										</div>
									</div>
								)
							) : (
								<div>
									<div className="spacedFormGroup">
										<div>
											<label>New Password</label>
											<input type="password" name="password" value={password} onChange={e => setPassword(e.target.value)} />
										</div>

										<div>
											<label>Confirm Password</label>
											<input type="password" name="password2" value={password2} onChange={e => setPassword2(e.target.value)} />
										</div>
									</div>

									<div className="spacedFormGroup">
										<button onClick={resetPassword}>Reset Password</button>
									</div>
								</div>
							)
						}
					</div>
				)}
			</div>
		</div>
	) : <Navigate to="/auth/login" />;
}
